import { useAtom, useAtomValue } from "jotai";
import { Navigate, NavLink, Outlet, useLoaderData, useLocation, useSearchParams } from "react-router-dom";
import { authAtom, redirectAtom, balancesAtom, actingAsAtom } from "./Data/Atoms";
import { Badge, Col, Container, ListGroup, Row } from "react-bootstrap";
import { ArrowRepeat, ArrowUpCircle, Calendar2Check, CarFrontFill, CreditCard, CurrencyDollar, House, IconProps, InboxFill, PeopleFill, QrCodeScan } from "react-bootstrap-icons";
import { PageTitle } from "./Controls/PageTitle";
import { ReactNode } from "react";
import { cents, Money, showMoneyShort, toCents } from "./Data/Money";
import { FacilityParkerBalance } from "./Data/ApiTypes";
import { add, equal } from "dinero.js";
import React from "react";

export interface UserPage {
    children: ReactNode;
    url:      string;
}

interface MyIconProps {
    size?:        number;
    strokeWidth?: number;
}

export interface MenuItemProps extends UserPage {
    onClick?:   () => void;
    className?: string;
    end?:       boolean;
    active?:    boolean;
    icon:     React.ReactElement<MyIconProps>;
}

export function MenuItem( props: MenuItemProps ) {
    const {
        children,
        url,
        active = false,
        end = false,
        className = `${active ? "my-active" : ""}`,
        onClick = () => 0
    } = props;
    const iconWithProps = React.cloneElement<IconProps>( props.icon, {
        size: props.icon.props.size !== undefined ? 24 : props.icon.props.size,
    } );
    return <ListGroup.Item key={url}
                           as={NavLink}
                           to={url}     //props on NavLink
                           end={end}    //props on NavLink
                           className={className}
                           onClick={onClick}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {iconWithProps}
            <span>{children}</span>
        </div>
    </ListGroup.Item>;
}

export function OrdersMenuBadge( { balance }: { balance: Money } ) {
    const zero = cents( 0 );
    const isPaid = equal( balance, zero );
    return <Badge className="border border-primary bg-white fw-normal text-primary">
        {isPaid ? "Paid" : `${showMoneyShort( balance )} Due`}
    </Badge>;
}

export function AppUser() {
    const loc  = useLocation();
    const [searchParams] = useSearchParams();
    const [redirect, setRedirect] = useAtom( redirectAtom );
    const auth      = useAtomValue( authAtom );
    const actingAs  = useAtomValue( actingAsAtom );
    const balances  = useLoaderData() as FacilityParkerBalance[];
    const [, setBalances] = useAtom( balancesAtom );
    const totalDebt = balances.map( b => b.balance ).reduce( (a, b) => add( a, b ), cents( 0 ) );

    // Set the balances in the atom so other components can access them
    React.useEffect(() => {
        setBalances(balances);
    }, [balances, setBalances]);

    if( !auth.isLoggedIn ) {
        setRedirect( loc.pathname );
        const redirectToResetCode: string | null = searchParams.get( "code" );
        if( redirectToResetCode ) {            
            return <Navigate to={`/reset?code=${redirectToResetCode}`} />;
        }
        return <Navigate to="/login" />;
    }

    //some stuff for handling mobile and
    //desktop versions of these pages
    //be careful modifying this, it is tricky
    const menuClassName    = loc.pathname === "/user"     ? ""       : "d-none";
    const contentClassName = loc.pathname === "/user"     ? "d-none" : "";
    const activeOverride   = loc.pathname === ( `/user` ) ? true     : false;

    return <Container className="pt-2">
        <Row>
            <Col md={4} className={`${menuClassName} d-md-block`}>
                <PageTitle>
                    Account
                </PageTitle>
                <ListGroup>
                
                    {!actingAs && <MenuItem icon={<House/>} url="/user/profile" active={activeOverride}>     Overview          </MenuItem>}
                    <MenuItem icon={<QrCodeScan/>} url="/user/access-codes">                   Access Passes     </MenuItem>
                    <MenuItem icon={<Calendar2Check/>} url="/user/reservation/upcoming">       Reservations      </MenuItem>
                    <MenuItem icon={<ArrowRepeat/>} url="/user/plan">                          Plans             </MenuItem>
                    <MenuItem icon={<InboxFill/>} url="/user/order" className="d-flex align-items-center gap-1">
                        Orders {toCents( totalDebt ) > 0 && <OrdersMenuBadge balance={totalDebt} />}
                    </MenuItem>
                    <MenuItem icon={<CurrencyDollar/>} url="/user/payment">                     Payments         </MenuItem>
                    <MenuItem icon={<CarFrontFill/>} url="/user/vehicle">                       Vehicles         </MenuItem>
                    <MenuItem icon={<CreditCard/>} url="/user/billing">                         Payment Methods          </MenuItem>
                    {!actingAs && <MenuItem icon={<PeopleFill/>} url="/user/delegates">          Users            </MenuItem>}
                </ListGroup>
            </Col>
           <Col md={8} className={`${contentClassName} p-0 m-0 d-md-block`}>
                <div className="mx-2">
                    <Outlet />
                </div>
            </Col>
        </Row>
    </Container>;
}